import { Database, DatabaseNodeListResponse } from '@couchbasecloud/base-ui/types/database-service.types';
import { ValidationError } from 'components/database-service-groups/database-service-groups.types';
import { IconName } from 'components/icon';
import { Provider } from 'constants/provider';
import {
  CAPELLA_SERVICE,
  CapellaService,
  CapellaServiceName,
  SERVER_SERVICE,
  SERVER_SERVICE_ICONS,
  ServerService,
} from 'constants/service';
import { specsResponseToServiceGroup } from 'pages/project/database/create-database/create-database.utils';
import {
  DatabaseProviderDeploymentOptions,
  DatabaseServiceConfig,
  DatabaseSpec,
  DatabaseSpecData,
  DatabaseSpecResponseWithId,
  DatabaseSpecWithId,
  DatabaseSpecWithIdV2,
  DeploymentRegionsV2Response,
  DeploymentServiceGroupsV2Response,
  NewSpecOptions,
  UpdateDatabaseSpecPayload,
} from 'sync/configuration-service';
import { Option } from 'types/options';
import { differenceWith, unionWith } from 'utils/lists';
import { resolveOption } from 'utils/resolve-option';

// we use this when current node is single, and we try to convert it to a multi-node

export const isSingleMultiNode = (spec?: DatabaseSpecWithIdV2) => {
  return !!(spec && spec.count > 1 && spec.originalCount === 1);
};

export const getServiceLabel = (service: CapellaService) => CAPELLA_SERVICE[service];

export const getNodeCount = (response: DatabaseSpecResponseWithId) => {
  return (
    response?.data?.specs?.reduce((accumulator, currentValue) => {
      return accumulator + (currentValue?.count || 0);
    }, 0) || 0
  );
};

export const getNodeCountFromServices = (services: Database['services']) => {
  return (
    services?.reduce((accumulator, currentValue) => {
      return accumulator + (currentValue?.count || 0);
    }, 0) || 0
  );
};

/**
 * Returns the count of {service} nodes
 * @param {DatabaseNodeListResponse} list The list of database nodes
 * @param {CapellaServiceName} service The service we are interested in
 * @return {number} the count of {service} nodes
 */
export const getServiceNodeCount = (list: DatabaseNodeListResponse, service: CapellaServiceName) => {
  return list.data.filter((node) => node.data.services.includes(service)).length;
};

export const hasChanged = (previousDatabaseSpec: DatabaseSpec[], currentDatabaseSpec: DatabaseSpec[]) => {
  if (previousDatabaseSpec.length !== currentDatabaseSpec.length) {
    return true;
  }

  return currentDatabaseSpec.reduce((accumulator, currentValue, index) => {
    if (accumulator) {
      return true;
    }
    const previousDatabaseSpecValue = previousDatabaseSpec[index];
    return (
      currentValue.compute !== previousDatabaseSpecValue.compute ||
      currentValue.count !== previousDatabaseSpecValue.count ||
      currentValue.services.length !== previousDatabaseSpecValue.services.length ||
      currentValue.disk.sizeInGb !== previousDatabaseSpecValue.disk.sizeInGb ||
      currentValue.disk.iops !== previousDatabaseSpecValue.disk.iops ||
      currentValue.services.some((svc) => !previousDatabaseSpecValue.services.includes(svc))
    );
  }, false);
};

/**
 * Returns whether there are nodes
 * @param {DatabaseNodeListResponse} list The list of database nodes
 * @return {boolean} Whether there are any nodes
 */
export const hasNodes = (list: DatabaseNodeListResponse) => {
  return list.data.length !== 0;
};

const hasService =
  <T>(svc: T) =>
  (serviceList: T[]) => {
    return serviceList.includes(svc);
  };

export const hasCbDataService = hasService<ServerService>('kv');

/**
 * Returns whether there is an unhealthy
 * @param {DatabaseNodeListResponse} list The list of database nodes
 * @return {boolean} Whether there is an Unhealthy node
 */
export const hasUnhealthyNode = (list: DatabaseNodeListResponse) => {
  return list.data.some((node) => node.data.status.state === 'Unhealthy');
};

export const SERVICE_LABELS: Record<ServerService, { icon: IconName; description: string; label: string }> = {
  kv: {
    description: 'Stores and manages the data within the Cluster',
    icon: SERVER_SERVICE_ICONS.kv,
    label: 'Data',
  },
  index: {
    description: 'Primary and Secondary indexes for optimal query performance',
    icon: SERVER_SERVICE_ICONS.index,
    label: 'Index',
  },
  n1ql: {
    description: 'Use for operational queries, like the front-end queries behind every page display or navigation',
    icon: SERVER_SERVICE_ICONS.n1ql,
    label: 'Query',
  },
  cbas: {
    description: "Use when don't know every aspect of the query in advance",
    icon: SERVER_SERVICE_ICONS.cbas,
    label: 'Analytics',
  },
  fts: {
    description: 'Use when you want to take advantage of natural-language querying to create, manage, and query specially-purposed indexes',
    icon: SERVER_SERVICE_ICONS.fts,
    label: 'Full Text Search',
  },
  eventing: {
    description: 'Use to streamline your business workflows',
    icon: SERVER_SERVICE_ICONS.eventing,
    label: 'Eventing',
  },
};

export const toUpdatePayload = (specs: DatabaseSpecWithId[]): UpdateDatabaseSpecPayload => {
  const data = specs.map((spec) => ({
    compute: { type: spec.compute },
    count: spec.count,
    disk: {
      type: spec.disk.type,
      sizeInGb: spec.disk.sizeInGb,
      iops: spec.disk.iops,
    },
    diskAutoScaling: {
      enabled: spec.diskAutoScaling.enabled,
    },
    services: spec.services.map((svc) => ({ type: svc })),
  }));
  return {
    specs: data,
  };
};

export const getProviderOptions = (databaseProviderDeploymentOptions: DatabaseProviderDeploymentOptions) => {
  let versionOptions: Partial<Option[]> = [];
  if (databaseProviderDeploymentOptions.serverVersions) {
    versionOptions = databaseProviderDeploymentOptions.serverVersions.versions.map((version) => ({
      label: version,
      value: version,
    }));
  }

  const region = databaseProviderDeploymentOptions.provider.regions[0].key;
  const server = databaseProviderDeploymentOptions.serverVersions?.defaultVersion;

  return {
    versionOptions,
    region,
    server,
  };
};

export const getAvailableServices = (specResponse?: DatabaseSpecData, specs?: DatabaseSpecWithIdV2[]): ServerService[] => {
  const allServices = resolveOption(
    isSingleMultiNode(specs && specs[0]),
    specResponse?.alternativeNewSpecOptions?.services,
    specResponse?.newSpecOptions?.services
  );

  const usedServices = new Set((specs || []).flatMap((spec) => spec.services));

  return (allServices || []).reduce((result: ServerService[], config) => {
    if (!usedServices.has(config.service.key)) {
      result.push(config.service.key);
    }
    return result;
  }, []);
};

export const getAvailableServicesV2 = ({
  groups,
  specs,
}: {
  groups?: DeploymentServiceGroupsV2Response['serviceGroupsTemplates'][number]['serviceGroups'];
  specs: DatabaseSpecWithIdV2[];
}) => {
  if (!groups) {
    return [];
  }
  const selectedService = specs.reduce<{
    [key in ServerService]?: boolean;
  }>((acc, spec) => {
    spec.services.forEach((service) => {
      acc[service] = true;
    });
    return acc;
  }, {});
  const availableServices = groups.reduce<{
    [key in ServerService]?: boolean;
  }>((acc, spec) => {
    spec.services.forEach((service) => {
      acc[service.key] = true;
    });
    return acc;
  }, {});
  return (Object.keys(availableServices) as ServerService[]).filter((service) => !selectedService[service]);
};

export const addServiceGroupV2 = ({
  template,
  specs,
  storages,
  computes,
  selectedProvider,
  availableServices,
  storageAutoExpansion,
  nextService,
}: {
  template: DeploymentServiceGroupsV2Response['serviceGroupsTemplates'][number];
  specs: DatabaseSpecWithIdV2[];
  storages: DeploymentServiceGroupsV2Response['storage'];
  computes: DeploymentServiceGroupsV2Response['compute'];
  storageAutoExpansion: DeploymentRegionsV2Response['storageAutoExpansion'];
  selectedProvider: Provider;
  availableServices: ServerService[];
  nextService: ServerService;
}): DatabaseSpecWithIdV2 => {
  const selectedGroupsIds = specs.reduce<{ [key: number]: boolean }>((acc, spec) => {
    acc[spec.id] = true;
    return acc;
  }, {});
  let serviceGroupId = 0;
  const serviceGroup = template.serviceGroups.find((group, index) => {
    if (!selectedGroupsIds[index] && !!group.services.find((v) => availableServices.includes(v.key))) {
      serviceGroupId = index;
      return true;
    }
    return false;
  })!;

  return specsResponseToServiceGroup(
    serviceGroupId,
    serviceGroup,
    template,
    selectedProvider,
    [nextService],
    computes,
    storages,
    storageAutoExpansion
  );
};

export const getStorage = (spec: DatabaseSpecWithIdV2, size?: number) => {
  const diskOptions = resolveOption(isSingleMultiNode(spec), spec.diskAlternativeOptions, spec.diskOptions);

  const storage = (diskOptions || []).find((storage) => storage.key === spec.disk.type);
  if (!storage) {
    return null;
  }
  if (size !== undefined) {
    return storage.size.find((size1) => size >= size1.min && size <= size1.max) || storage.size[0];
  }
  return storage.size[0];
};

export const getStorageRange = (spec: DatabaseSpecWithIdV2) => {
  const diskOptions = resolveOption(isSingleMultiNode(spec), spec.diskAlternativeOptions, spec.diskOptions);

  const storage = (diskOptions || []).find((storage) => storage.key === spec.disk.type);
  if (!storage) {
    return [undefined, undefined];
  }

  return (
    spec.restrictedToSizesInGb ? storage.size.filter((value) => spec.restrictedToSizesInGb!.includes(value.max)) : storage.size
  ).reduce<[number | undefined, number | undefined]>(
    (acc, item) => {
      if (acc[0] === undefined || item.min < acc[0]) {
        acc[0] = item.min;
      }
      if (acc[1] === undefined || item.max > acc[1]) {
        acc[1] = item.max;
      }

      return acc;
    },
    [undefined, undefined]
  );
};

const validateIops = (spec: DatabaseSpecWithIdV2, size?: number): string | undefined => {
  const storage = getStorage(spec, size);
  if (!storage) {
    return undefined;
  }

  const { min, max } = storage.iops || {};

  if ((min && min > (spec.disk.iops ?? min)) || (max && max < (spec.disk.iops ?? max))) {
    return `Please enter an IOPS value between ${min} and ${max}.`;
  }
  return undefined;
};

const validateDiskSize = (spec: DatabaseSpecWithIdV2): string | undefined => {
  const [minSize, maxSize] = getStorageRange(spec);
  // cp-ui has the following error message when reducing storage size from default:
  // "It is not possible to reduce storage size"

  if (minSize && maxSize && (minSize > spec.disk.sizeInGb || maxSize < spec.disk.sizeInGb)) {
    return `Please enter a storage size between ${minSize} and ${maxSize}.`;
  }
  return undefined;
};

const validateCompute = (spec: DatabaseSpecWithIdV2) => {
  if (
    !spec.compute
    // TODO: cp-api should be returning this value (only) in the computeOptions list
    // || !spec.computeOptions.some((compute) => compute.key === spec.compute)
  ) {
    return 'Invalid compute value';
  }
  return undefined;
};

/**
 * Returns the number of nodes available for upgrade.
 */
export const getAvailableNodes = (specs: DatabaseSpecWithIdV2[], maxNodeCount: number): number => {
  return maxNodeCount - specs.reduce((acc, curr) => acc + curr.count, 0);
};

const validateCount = (specs: DatabaseSpecWithIdV2[], spec: DatabaseSpecWithIdV2) => {
  const { min, max } = spec.nodes;

  if (spec.count < min) {
    return `Please enter a value more than ${min - 1}`;
  }

  if (spec.count > max) {
    return `Please enter a value less than ${max + 1}`;
  }

  const available = getAvailableNodes(specs, spec.totalNodes.max) + spec.count;
  if (spec.count > available) {
    return `Total number of nodes should not exceed ${spec.totalNodes.max}.`;
  }

  return undefined;
};

const validateServices = (specs: DatabaseSpecWithIdV2[], spec: DatabaseSpecWithIdV2, database?: Database) => {
  const serviceOptions = resolveOption(isSingleMultiNode(spec), spec.serviceAlternativeOptions, spec.serviceOptions);
  const requiredServices = (serviceOptions || []).filter((svc) => svc.required);
  const selectedServices = unionWith(
    (a: ServerService, b: ServerService) => a === b,
    (specs || []).reduce((acc: ServerService[], curr) => {
      return acc.concat(curr.services);
    }, [])
  );
  const missingRequired = differenceWith(
    (a: string, b: string) => a === b,
    requiredServices.map((svc) => svc.key),
    selectedServices
  ).map((svc) => {
    return requiredServices.find((s) => svc === s.key);
  });
  if (missingRequired.length) {
    switch (missingRequired.length) {
      case 1:
        return `The "${missingRequired[0]?.key}" service is required.`;
      default:
        return `The "${missingRequired.map((svc) => svc?.key).join('", "')}" services are required.`;
    }
  }
  if (spec.services.length === 0) {
    return 'Please select at least one service.';
  }
  // A Database that has an associated App Service must have the index and query services

  if (database?.appService?.id) {
    let includesIndex = false;
    let includesQuery = false;
    specs.forEach((svc) => {
      if (svc.services.includes('index')) {
        includesIndex = true;
      }
      if (svc.services.includes('n1ql')) {
        includesQuery = true;
      }
    });

    if (!includesIndex && !includesQuery) {
      return `The ${SERVER_SERVICE.index} and ${SERVER_SERVICE.n1ql} services are required for Databases that have a linked App Service.`;
    }
    if (!includesIndex) {
      return `The ${SERVER_SERVICE.index} service is required for Databases that have a linked App Service.`;
    }
    if (!includesQuery) {
      return `The ${SERVER_SERVICE.n1ql} service is required for Databases that have a linked App Service.`;
    }
  }
  return undefined;
};

export const validateGroupsServices = (specs: DatabaseSpecWithIdV2[], database?: Database) => {
  const errors: ValidationError[] = [];

  specs.forEach((spec, index) => {
    const iopsMessage = validateIops(spec, spec.disk.sizeInGb);

    if (iopsMessage) {
      errors.push({ index, field: 'iops', message: iopsMessage });
    }
    const computeMessage = validateCompute(spec);
    if (computeMessage) {
      errors.push({ index, field: 'compute', message: computeMessage });
    }
    const countMessage = validateCount(specs, spec);
    if (countMessage) {
      errors.push({ index, field: 'count', message: countMessage });
    }

    const diskSizeMessage = validateDiskSize(spec);
    if (diskSizeMessage) {
      errors.push({ index, field: 'diskSize', message: diskSizeMessage });
    }

    const servicesMessage = validateServices(specs, spec, database);
    if (servicesMessage) {
      errors.push({ index, field: 'services', message: servicesMessage });
    }
  });
  return errors;
};

export function parseSpecsToUpdatePayload(specs: DatabaseSpecWithIdV2[]): UpdateDatabaseSpecPayload {
  const data = specs.map(({ compute, count, disk, diskAutoScaling, services }) => ({
    count,
    compute: { type: compute },
    disk: {
      type: disk.type,
      sizeInGb: disk.sizeInGb,
      iops: disk.iops,
    },
    diskAutoScaling,
    services: services.map((svc) => ({ type: svc })),
  }));

  return {
    specs: data,
  };
}

export function getMinServiceNodes(serviceOptions: DatabaseServiceConfig[], services: Partial<ServerService>[]) {
  return serviceOptions
    .filter((option) => services.includes(option.service.key))
    .reduce((max, option) => Math.max(max, option.requirements.min), 1);
}

function generateComputeOptions(computeOptions?: DatabaseSpec['computeOptions']) {
  return (computeOptions || [])
    .map(({ compute: { key, vcpus, memory } }) => ({
      key,
      vcpus,
      memory,
    }))
    .sort((optionA, optionB) => parseFloat(optionA.vcpus) - parseFloat(optionB.vcpus));
}

function generateServiceOptions(services?: NewSpecOptions['services']) {
  return (services || []).map((config) => ({
    key: config.service.key,
    default: config.eligibility.default,
    required: config.requirements.required,
  }));
}

export function servicesResponseToServiceGroups(specResponse?: DatabaseSpecData, provider?: Provider) {
  const serviceGroups: DatabaseSpecWithIdV2[] = [];

  const newSpecOptions = specResponse?.newSpecOptions;
  const alternativeNewSpecOptions = specResponse?.alternativeNewSpecOptions;

  if (!provider || !newSpecOptions) {
    return serviceGroups;
  }

  const serviceOptions = generateServiceOptions(newSpecOptions.services);

  specResponse.specs.forEach((spec, index) => {
    serviceGroups.push({
      id: index,
      key: 'specs',
      nodes: {
        min: getMinServiceNodes(newSpecOptions?.services, spec.services),
        max: spec.count === 1 ? alternativeNewSpecOptions?.nodeCount?.max || 1 : newSpecOptions?.nodeCount?.max,
      },
      provider,
      originalCount: spec.count,
      count: spec.count,
      totalNodes: spec.count === 1 ? alternativeNewSpecOptions?.nodeCount || { min: 1, max: 1 } : newSpecOptions?.nodeCount,
      services: spec.services,
      compute: spec.compute,
      serviceOptions:
        serviceOptions.length === 0
          ? spec.services.map((service) => ({
              key: service,
              default: true,
              required: true,
            }))
          : serviceOptions,
      serviceAlternativeOptions: generateServiceOptions(specResponse?.alternativeNewSpecOptions?.services),
      computeOptions: generateComputeOptions(spec.computeOptions),
      computeAlternativeOptions: generateComputeOptions(spec.computeAlternativeOptions),
      disk: spec.disk,
      diskOptions: spec.diskOptions,
      diskAlternativeOptions: spec.diskAlternativeOptions,
      ...(newSpecOptions?.diskAutoScaling?.enabled
        ? {
            diskAutoScaling: {
              enabled: Boolean(newSpecOptions?.diskAutoScaling?.default),
            },
          }
        : {}),
    });
  });

  return serviceGroups;
}

export function getCountOptions(minCount: number, maxCount: number): Option<number>[] {
  const options: Option<number>[] = [];

  for (let i = minCount; i <= maxCount; i++) {
    // as single node can't have an option with 2 and single node only reside in first service group so we are skipping 2
    if (!(minCount === 1 && i === 2)) {
      options.push({
        label: i.toString(),
        value: i,
      });
    }
  }
  return options;
}
